import { useState, useEffect, useRef } from 'react';

export default function SistersSection() {
  const [wiggled, setWiggled] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setWiggled(true), 200);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="sisters" className="w-full py-10 md:py-20 bg-primary-100 relative overflow-hidden scroll-mt-20" ref={sectionRef}>
      {/* Paw print decorations */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <div className="w-16 h-16 flex items-center justify-center absolute top-8 left-[5%] opacity-[0.06] rotate-[-15deg] animate-float" style={{ animationDelay: '0s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-16 h-16 flex items-center justify-center absolute top-20 right-[8%] opacity-[0.05] rotate-[20deg] animate-drift" style={{ animationDelay: '0.7s' }}>
          <i className="fa-solid fa-paw text-6xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute top-1/2 left-[3%] opacity-[0.07] rotate-[35deg] animate-float" style={{ animationDelay: '1.4s' }}>
          <i className="fa-solid fa-paw text-4xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute top-[45%] right-[5%] opacity-[0.05] rotate-[-25deg] animate-drift" style={{ animationDelay: '2.1s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute bottom-24 left-[12%] opacity-[0.06] rotate-[10deg] animate-float" style={{ animationDelay: '2.8s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute bottom-16 right-[15%] opacity-[0.07] rotate-[-40deg] animate-drift" style={{ animationDelay: '3.5s' }}>
          <i className="fa-solid fa-paw text-4xl text-foreground-950"></i>
        </div>
        <div className="w-20 h-20 flex items-center justify-center absolute top-[30%] left-1/2 opacity-[0.04] rotate-[45deg] animate-float" style={{ animationDelay: '4.2s' }}>
          <i className="fa-solid fa-paw text-7xl text-foreground-950"></i>
        </div>
        <div className="w-12 h-12 flex items-center justify-center absolute bottom-[35%] left-[20%] opacity-[0.05] rotate-[-10deg] animate-drift" style={{ animationDelay: '5.0s' }}>
          <i className="fa-solid fa-paw text-3xl text-foreground-950"></i>
        </div>
        <div className="w-16 h-16 flex items-center justify-center absolute top-[15%] right-[25%] opacity-[0.06] rotate-[60deg] animate-float" style={{ animationDelay: '3.0s' }}>
          <i className="fa-solid fa-paw text-5xl text-foreground-950"></i>
        </div>
        <div className="w-14 h-14 flex items-center justify-center absolute bottom-8 right-[35%] opacity-[0.05] rotate-[-55deg] animate-drift" style={{ animationDelay: '1.8s' }}>
          <i className="fa-solid fa-paw text-4xl text-foreground-950"></i>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 relative z-10">
        <style>{`
          @keyframes wiggle {
            0% { transform: rotate(0deg); }
            15% { transform: rotate(-5deg); }
            30% { transform: rotate(4deg); }
            45% { transform: rotate(-3deg); }
            60% { transform: rotate(2deg); }
            75% { transform: rotate(-1deg); }
            100% { transform: rotate(0deg); }
          }
          .animate-wiggle {
            animation: wiggle 0.7s ease-in-out;
          }
        `}</style>
        <h2 className="text-center text-3xl md:text-4xl font-black text-primary-500 mb-3">Meet the Adventure Sisters</h2>
        <p className="text-center text-foreground-700 text-base md:text-lg mb-8 md:mb-14 max-w-2xl mx-auto">
          Real-life Bichon Frise sisters whose magical adventures inspire children to be brave, kind, and curious!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-8 md:mb-12">
          <div className="bg-white/80 rounded-2xl overflow-hidden flex flex-col">
            <div className="w-full bg-primary-200 flex items-center justify-center p-4">
              <img
                alt="Gracie, the white fluffy Bichon Frise sister from Gracie & Lily Adventures, wearing her red collar and red flower"
                title="Gracie the adventurous Bichon Frise from Gracie & Lily Adventures children's books"
                className={`w-full h-auto max-h-80 object-contain rounded-2xl ring-4 ring-white drop-shadow-[0_8px_20px_rgba(0,0,0,0.18)] cursor-pointer hover:scale-105 transition-transform duration-300 ${wiggled ? 'animate-wiggle' : ''}`}
                src="https://storage.readdy-site.link/project_files/68a07134-6060-4041-8161-9728c6c3b4d7/af329b9c-13fa-4a60-b5b7-75a34b96f380_Wave-red.png"
              />
            </div>
            <div className="p-6 md:p-8 flex flex-col gap-3">
              <p className="text-accent-400 font-bold text-sm">🌟 Gracie</p>
              <h3 className="text-xl md:text-2xl font-black text-primary-500">Gracie - The Brave Explorer</h3>
              <p className="text-foreground-700 leading-relaxed text-sm">
                Gracie is adventurous, curious, and always ready to explore new places! She loves chasing butterflies, discovering hidden treasures, and leading the way on exciting adventures.
              </p>
              <p className="font-extrabold text-primary-500 text-sm mt-2">Brave • Adventurous • Curious</p>
            </div>
          </div>

          <div className="bg-white/80 rounded-2xl overflow-hidden flex flex-col">
            <div className="w-full bg-primary-50 flex items-center justify-center p-4">
              <img
                alt="Lily, the white fluffy Bichon Frise sister from Gracie & Lily Adventures, wearing her blue collar and blue flower"
                title="Lily the kind Bichon Frise from Gracie & Lily Adventures children's books"
                className={`w-full h-auto max-h-80 object-contain rounded-2xl ring-4 ring-white drop-shadow-[0_8px_20px_rgba(0,0,0,0.18)] cursor-pointer hover:scale-105 transition-transform duration-300 ${wiggled ? 'animate-wiggle' : ''}`}
                src="https://storage.readdy-site.link/project_files/68a07134-6060-4041-8161-9728c6c3b4d7/9b9fed9b-8990-4685-825d-68d3f51ad36b_blue-dog.png"
              />
            </div>
            <div className="p-6 md:p-8 flex flex-col gap-3">
              <p className="text-accent-500 font-bold text-sm">💖 Lily</p>
              <h3 className="text-xl md:text-2xl font-black text-primary-500">Lily - The Kind Heart</h3>
              <p className="text-foreground-700 leading-relaxed text-sm">
                Lily is sweet, caring, and loves making new friends! She spreads kindness wherever she goes, helps those in need, and brings joy to everyone she meets in the magical woods.
              </p>
              <p className="font-extrabold text-primary-500 text-sm mt-2">Kind • Caring • Friendly</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5">
          <a
            href="https://www.youtube.com/@GracieandLilyAdventures"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary-500 text-white px-6 md:px-8 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors text-center"
          >
            Watch Storytime Videos — Join our YouTube Channel
          </a>
          <a
            href="https://www.youtube.com/@GracieandLilyAdventures"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full max-w-xs rounded-2xl overflow-hidden cursor-pointer"
          >
            <img
              alt="Gracie and Lily Adventures YouTube channel with storytime videos for children"
              title="Gracie & Lily Adventures storytime videos on YouTube"
              className="w-full h-auto object-contain"
              src="https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/z4s3rpnzx64fspv/c1c31d5df7aa4ace92bb1a1c76a339e3/original/youtube-2.png"
            />
          </a>
        </div>
      </div>
    </section>
  );
}